import React from "react";

const RETRY_SESSION_KEY = "pm:lazy-import-retry";
let reloadScheduled = false;

const getCurrentRouteToken = () => {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname || "/"}${window.location.search || ""}`;
};

const getErrorText = (error) => {
  if (!error) return "";
  const parts = [
    error.name,
    error.message,
    typeof error === "string" ? error : "",
    error?.stack,
  ].filter(Boolean);
  return parts.join(" ").toLowerCase();
};

export const isLikelyLazyImportFailure = (error) => {
  const text = getErrorText(error);
  if (!text) return false;

  return [
    "chunkloaderror",
    "loading chunk",
    "failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "importing a module script failed",
    "unexpected token '<'",
    "/static/js/",
    ".chunk.js",
  ].some((needle) => text.includes(needle));
};

const hasRetriedCurrentRoute = (token) => {
  if (!token || typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(RETRY_SESSION_KEY) === token;
  } catch (_) {
    return reloadScheduled;
  }
};

const markRetriedCurrentRoute = (token) => {
  if (!token || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(RETRY_SESSION_KEY, token);
  } catch (_) {
    // Ignore storage failures and still attempt the reload.
  }
};

const clearRetriedCurrentRoute = (token) => {
  if (!token || typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(RETRY_SESSION_KEY) === token) {
      window.sessionStorage.removeItem(RETRY_SESSION_KEY);
    }
  } catch (_) {
    // Ignore storage failures.
  }
};

const reloadOnce = () => {
  if (reloadScheduled || typeof window === "undefined") return;
  reloadScheduled = true;
  window.location.reload();
};

export default function lazyWithRetry(loader) {
  return React.lazy(() =>
    loader()
      .then((module) => {
        clearRetriedCurrentRoute(getCurrentRouteToken());
        return module;
      })
      .catch((error) => {
        const routeToken = getCurrentRouteToken();
        if (
          typeof window !== "undefined" &&
          isLikelyLazyImportFailure(error) &&
          !hasRetriedCurrentRoute(routeToken)
        ) {
          markRetriedCurrentRoute(routeToken);
          reloadOnce();
          return new Promise(() => {});
        }

        clearRetriedCurrentRoute(routeToken);
        throw error;
      })
  );
}
